import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

export interface InquiryModalProps {
  open: boolean;
  onClose: () => void;
}

const SERVICE_OPTIONS: { groupKey: string; optionKeys: string[] }[] = [
  {
    groupKey: "inquiry.serviceGroupAccounting",
    optionKeys: [
      "inquiry.serviceSubscription",
      "inquiry.serviceChiefAccountant",
      "inquiry.serviceTaxReview",
    ],
  },
  {
    groupKey: "inquiry.serviceGroupPayroll",
    optionKeys: [
      "inquiry.servicePayrollSubscription",
      "inquiry.serviceHRConsult",
      "inquiry.serviceHRReview",
    ],
  },
  {
    groupKey: "inquiry.serviceGroupTax",
    optionKeys: ["inquiry.serviceTaxCase"],
  },
];
const INQUIRY_API_URL = "https://api.balkanfin.com/api/inquiry";

export function InquiryModal({ open, onClose }: InquiryModalProps): React.ReactElement {
  const { t } = useTranslation();

  const [service, setService] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [eik, setEik] = useState("");
  const [activity, setActivity] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [needs, setNeeds] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const resetForm = useCallback(() => {
    setService("");
    setCompanyName("");
    setEik("");
    setActivity("");
    setContactPerson("");
    setPhone("");
    setEmail("");
    setNeeds("");
    setSubmitError("");
    setIsSubmitting(false);
    setSubmitSuccess(false);
  }, []);

  const handleModalClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") handleModalClose();
    },
    [handleModalClose]
  );

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, handleEscape]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    const serviceLabel = service ? t(service) : "";
    try {
      setIsSubmitting(true);
      const payload = {
        service: serviceLabel,
        serviceKey: service,
        companyName,
        eik,
        activity,
        contactPerson,
        phone,
        email,
        needs,
      };

      console.log("[Inquiry form] Payload", payload);
      const response = await fetch(INQUIRY_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Inquiry request failed with status ${response.status}`);
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        handleModalClose();
      }, 2000);
    } catch (error) {
      console.error("[Inquiry form] Submission error", error);
      setSubmitError("Could not send your inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return <></>;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={handleModalClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="inquiry-modal-title"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 id="inquiry-modal-title" className="text-xl font-semibold text-navy">
            {t("inquiry.title")}
          </h2>
          <button
            type="button"
            onClick={handleModalClose}
            className="relative w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-navy/20"
            aria-label={t("nav.close")}
          >
            <span className="sr-only">{t("nav.close")}</span>
            <span className="absolute w-5 h-0.5 bg-current rounded rotate-45" />
            <span className="absolute w-5 h-0.5 bg-current rounded -rotate-45" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="inquiry-service" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.service")} <span className="text-red-500">*</span>
            </label>
            <select
              id="inquiry-service"
              required
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold"
            >
              <option value="">{t("inquiry.service")}</option>
              {SERVICE_OPTIONS.map(({ groupKey, optionKeys }) => (
                <optgroup key={groupKey} label={t(groupKey)}>
                  {optionKeys.map((optionKey) => (
                    <option key={optionKey} value={optionKey}>
                      {t(optionKey)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="inquiry-company" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.companyName")} <span className="text-red-500">*</span>
            </label>
            <input
              id="inquiry-company"
              type="text"
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold"
            />
          </div>

          <div>
            <label htmlFor="inquiry-eik" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.eik")}
            </label>
            <input
              id="inquiry-eik"
              type="text"
              value={eik}
              onChange={(e) => setEik(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold"
            />
          </div>

          <div>
            <label htmlFor="inquiry-activity" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.activity")} <span className="text-red-500">*</span>
            </label>
            <input
              id="inquiry-activity"
              type="text"
              required
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold"
            />
          </div>

          <div>
            <label htmlFor="inquiry-contact" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.contactPerson")} <span className="text-red-500">*</span>
            </label>
            <input
              id="inquiry-contact"
              type="text"
              required
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold"
            />
          </div>

          <div>
            <label htmlFor="inquiry-phone" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.phone")} <span className="text-red-500">*</span>
            </label>
            <input
              id="inquiry-phone"
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold"
            />
          </div>

          <div>
            <label htmlFor="inquiry-email" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.email")} <span className="text-red-500">*</span>
            </label>
            <input
              id="inquiry-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold"
            />
          </div>

          <div>
            <label htmlFor="inquiry-needs" className="block text-sm font-medium text-gray-700 mb-1">
              {t("inquiry.needs")} <span className="text-red-500">*</span>
            </label>
            <textarea
              id="inquiry-needs"
              required
              rows={4}
              value={needs}
              onChange={(e) => setNeeds(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-navy focus:ring-2 focus:ring-gold/50 focus:border-gold resize-y"
            />
          </div>

          {submitSuccess && (
            <p className="text-green-700 text-sm" role="status">
              {t("inquiry.success")}
            </p>
          )}
          {submitError && (
            <p className="text-red-700 text-sm" role="alert">
              {submitError}
            </p>
          )}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-navy text-white font-medium rounded-xl hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy"
            >
              {isSubmitting ? "Submitting..." : t("inquiry.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default InquiryModal;
